import styled, { createGlobalStyle } from 'styled-components';
import { baseVariables, toRem } from '@marriott/mi-ui-library';
import { chatKeyboardFocusVisible } from './styles/chatFocus';

export const AISearchContainer = styled.div`
  display: flex;
  font-family: Swiss-721, Arial, Helvetica, sans-serif;
  background-color: var(--t-brand-bg-color, #ffffff);
  color: var(--t-brand-fg-color, #1c1c1c);

  /* Mobile defaults (< 768px) */
  flex-direction: column;
  overflow: hidden;
  width: 100vw;
  height: 100vh;
  margin-top: -51px;
  max-width: 100vw;

  /* Tablet/Desktop (768px and up) */
  @media ${baseVariables.mediaQuery.lg} {
    flex-direction: row;
    overflow: visible;
    height: calc(100vh - 88px);
    width: calc(100vw - 81px);
    margin-top: 0;
  }
`;

// Main content area (everything except the sidebar)
export const MainContentArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;

  /* Mobile defaults (< 768px) */
  height: 100%;

  /* Tablet/Desktop (768px and up) */
  @media ${baseVariables.mediaQuery.lg} {
    height: calc(100vh - 88px);
  }
`;

// Content below header (chat + preview)
export const ContentArea = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;

  /* Mobile defaults (< 768px) */
  flex-direction: column;
  height: calc(100% - 60px);

  /* Tablet/Desktop (768px and up) */
  @media ${baseVariables.mediaQuery.lg} {
    flex-direction: row;
    height: calc(100vh - 60px); /* Subtract header height */
  }
`;

export const ChatPanel = styled.div`
  /* 30% width on desktop; prevent shrinking */
  flex: 0 0 50%;
  min-width: 0;
  background: var(--t-brand-bg-color, #ffffff);
  border-right: 1px solid var(--t-horizontal-vertical-rule, #c4c4c4);
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: 100%;
    height: ${props => (props.theme?.showChat ? '100%' : '0')};
    flex: ${props => (props.theme?.showChat ? '1' : '0')};
    position: ${props => (props.theme?.showChat ? 'relative' : 'absolute')};
    transform: ${props => (props.theme?.showChat ? 'translateY(0)' : 'translateY(-100%)')};
    // transition: all 0.3s ease;
    overflow: hidden;
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
  }
`;

export const ChatHeader = styled.div`
  background: var(--t-input-fields-bg-color, #1c1c1c);
  color: var(--t-input-fields-fg-color, #ffffff);
  padding: 0.65rem;
  border-bottom: 1px solid var(--t-horizontal-vertical-rule, #c4c4c4);
  position: relative;
`;

// Unified Header Components - Travel Planning Layout
export const UnifiedHeader = styled.div`
  background: var(--color-set-brand-bg-color, #ffffff);
  border-bottom: 1px solid var(--Colors-neutral-20, #eee);
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: static;
  top: 0;
  z-index: 1;

  /* Mobile defaults (< 768px) - Following Figma mobile design exactly */
  padding: var(--general-spacer-02, 0.5rem) var(--general-spacer-03, 1rem);
  flex-wrap: nowrap;
  min-height: 48px;
  max-height: 48px;
  gap: var(--general-spacer-02, 0.5rem);

  /* Desktop enhancements (768px and up) - Following Figma desktop design */
  @media ${baseVariables.mediaQuery.lg} {
    padding: var(--general-spacer-02, 0.5rem) var(--general-spacer-03, 1rem);
    min-height: 60px;
    max-height: 60px;
    gap: var(--general-spacer-02, 0.5rem);
  }
`;

export const TripSection = styled.div`
  display: flex;
  align-items: center;
  gap: var(--general-spacer-02, 0.5rem);
  flex: 1;
  min-width: 0;

  /* Mobile specific adjustments - matches Figma mobile layout */
  @media (max-width: 768px) {
    max-width: calc(100% - 120px); /* Reserve space for toggle on mobile */
    gap: var(--general-spacer-01, 0.25rem);
    flex-shrink: 1;
  }

  /* Desktop adjustments - matches Figma desktop layout */
  @media ${baseVariables.mediaQuery.lg} {
    gap: var(--general-spacer-02, 0.5rem);
    max-width: none;
  }
`;

export const TripDropdown = styled.button`
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  font-family: var(--typography-subtitle-font-family, 'Swiss721_BT:Medium', sans-serif);
  color: var(--color-set-brand-fg-color, #1c1c1c);
  cursor: pointer;
  /* Adjusted padding to work with text section internal padding */
  padding: 0;
  transition: all 0.2s ease;
  position: relative;
  line-height: 1.25;
  letter-spacing: 0; /* Added letter-spacing to match Figma design */
  min-width: 0;
  ${chatKeyboardFocusVisible}

  &:hover {
    color: var(--color-set-brand-fg-color, #1c1c1c);
    cursor: pointer;
    background-color: var(--colors-neutral-10, #f4f4f4);
    border-radius: var(--general-spacer-02, 0.5rem);
  }

  /* Mobile optimizations - following Figma mobile design */
  @media (max-width: 768px) {
    font-size: var(--typography-t-subtitle-s-font-size, 0.875rem);
    max-width: 176px; /* Match Figma mobile trip section width */

    /* Match Figma typography for mobile */
    font-weight: var(--typography-subtitle-xl-font-weight, 700); /* Updated to match Figma design */
    line-height: 1.25;
  }

  /* Desktop - following Figma desktop design */
  @media ${baseVariables.mediaQuery.md} {
    font-size: var(--typography-t-subtitle-m-font-size, 1rem);
    max-width: none;
    font-weight: var(--typography-subtitle-xl-font-weight, 700); /* Updated to match Figma design */
  }
`;

export const TravelDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-left: 2rem;

  @media (max-width: 768px) {
    gap: 1rem;
    margin-left: 1rem;
    flex-wrap: wrap;
  }
`;

export const TravelItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`;

export const TravelLabel = styled.span`
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.025em;
`;

export const TravelValue = styled.span`
  font-size: 0.875rem;
  color: #1e293b;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 0 0 auto;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

interface HeaderActionButtonProps {
  primary?: boolean;
}

export const HeaderActionButton = styled.button<HeaderActionButtonProps>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => (props.primary ? '#3b82f6' : 'transparent')};
  color: ${props => (props.primary ? '#ffffff' : '#64748b')};
  border: ${props => (props.primary ? 'none' : '1px solid #e2e8f0')};
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${props => (props.primary ? '#2563eb' : '#f8fafc')};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
  }
`;

export const ChatTitle = styled.h2`
  font-size: 1.375rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: Swiss-721, Arial, Helvetica, sans-serif;
  color: inherit;
`;

export const ChatSubtitle = styled.p`
  font-size: 0.875rem;
  opacity: 0.9;
  margin: 0.5rem 0 0 0;
  font-weight: 500;
  line-height: 1.25rem;
`;

export const ChatIcon = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
`;

export const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &::-webkit-scrollbar {
    width: 0.375rem;
  }

  &::-webkit-scrollbar-track {
    background: var(--t-brand-bg-color, #ffffff);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--t-horizontal-vertical-rule, #c4c4c4);
    border-radius: 0.1875rem;
  }
`;

export const Message = styled.div<{ isUser?: boolean }>`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  ${props =>
    props.isUser &&
    `
    flex-direction: row-reverse;
  `}
`;

export const MessageAvatar = styled.div<{ isUser?: boolean }>`
  width: 2rem;
  height: 2rem;
  border-radius: var(--t-button-border-radius, 52px);
  background: ${props =>
    props.isUser ? 'var(--t-input-fields-bg-color, #1c1c1c)' : 'var(--t-icon-btn-primary-bg-color, #eeeeee)'};
  color: ${props => (props.isUser ? 'var(--t-input-fields-fg-color, #ffffff)' : 'var(--t-brand-fg-color, #1c1c1c)')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 700;
  flex-shrink: 0;
`;

export const MessageBubble = styled.div<{ isUser?: boolean; isError?: boolean }>`
  padding: ${props => (props.isUser ? '0.5rem 1rem' : '0.5rem 0')};
  border-radius: 1rem;
  background: ${props => (props.isUser ? '#FFF4F0' : '')};
  color: ${props => (props.isUser ? '#1c1c1c' : '#1c1c1c')};
  font-size: 0.875rem;
  line-height: 1.4;
  max-width: ${props => (props.isUser ? '500px' : '100%')};
  word-wrap: break-word;
  margin-right: ${props => (props.isUser ? '1.5rem' : '0')};

  ${props =>
    props.isUser
      ? `
    border-bottom-right-radius: 0.25rem;
  `
      : `
    border-bottom-left-radius: 0.25rem;
  `}
  ${props =>
    props.isError &&
    `
    width: 100%;
    padding-right: ${toRem(24)};
  `}

  /* Override markdown preview default styles */
  .wmde-markdown {
    background-color: transparent !important;
    color: inherit !important;
    font-family: inherit !important;
    font-size: inherit !important;
    line-height: inherit !important;
  }

  .wmde-markdown * {
    color: inherit !important;
    background-color: transparent !important;
  }

  .wmde-markdown img {
    height: 200px;
    width: 300px;
    object-fit: cover;
    border: 0px solid transparent;
  }

  /* HQV hotel-name links in markdown (WEB-195716) and external markdown anchors */
  .hqv-property-link,
  .wmde-markdown a {
    ${chatKeyboardFocusVisible}
  }
`;

export const ChatInput = styled.div`
  padding: 1rem;
  border-top: 1px solid var(--t-horizontal-vertical-rule, #c4c4c4);
  background: var(--t-brand-bg-color, #ffffff);
  position: relative;
`;

export const PreviewPanel = styled.div`
  // @media (min-width: 769px) {
  //   margin-left: 60px;
  // }
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--t-brand-bg-color, #ffffff);
  position: relative;
  /* Don't use overflow:hidden here — it breaks flex height resolution for children.
     Children manage their own overflow. */
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    height: ${props => (props.theme?.showChat ? '0' : '100%')};
    position: ${props => (props.theme?.showChat ? 'absolute' : 'relative')};
    transform: ${props => (props.theme?.showChat ? 'translateY(100%)' : 'translateY(0)')};
    // transition: all 0.3s ease;
    overflow: hidden;
  }

  .underline-content {
    text-decoration: underline;
  }

  // .m-map-pin {
  //   background-color: transparent !important;
  //   translate: 0px 6px;
  // }

  // .cluster-icon img {
  //   background-color: transparent;
  //   box-shadow: none;
  // }
`;

export const PreviewHeader = styled.div`
  padding: 1.4rem;
  border-bottom: 1px solid var(--t-horizontal-vertical-rule, #c4c4c4);
  background: var(--t-brand-bg-color, #ffffff);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

export const PreviewTitle = styled.h3`
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--t-brand-fg-color, #1c1c1c);
  margin: 0;
  flex: 1;
  font-family: Swiss-721, Arial, Helvetica, sans-serif;
`;

export const ViewToggle = styled.div`
  display: flex;
  background: var(--colors-neutral-10, #f4f4f4);
  border-radius: var(--general-spacer-02, 0.5rem);
  padding: var(--general-spacer-01, 0.25rem);
  gap: 0; /* No gap between buttons for segmented control per Figma */
  flex-shrink: 0;
  isolate: isolate; /* Match Figma segmented control isolation */

  /* Mobile specific dimensions - following Figma segmented control design */
  @media (max-width: 768px) {
    height: 32px;
    min-width: 96px; /* Increased to accommodate both buttons comfortably */
    display: flex; /* Show on mobile per Figma design */
  }

  /* Desktop - show segmented control per Figma design */
  @media ${baseVariables.mediaQuery.lg} {
    display: none;
  }
`;

export const ViewButton = styled.button<{ active?: boolean }>`
  border: none;
  border-radius: var(--general-spacer-01, 0.25rem);
  font-weight: var(--typography-subtitle-xl-font-weight, 500);
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: var(--typography-subtitle-font-family, 'Swiss721_BT:Medium', sans-serif);
  white-space: nowrap;
  flex: 1; /* Equal width buttons in segmented control */

  /* Colors based on active state - following Figma segmented control design */
  background: ${props => (props.active ? 'var(--colors-base-20, #ffffff)' : 'transparent')};
  color: ${props => (props.active ? 'var(--colors-base-10, #1c1c1c)' : 'var(--colors-neutral-40, #707070)')};
  box-shadow: ${props => (props.active ? '0px 2px 10px 0px rgba(0,0,0,0.07)' : 'none')};
  z-index: ${props => (props.active ? '1' : '2')}; /* Active button on top per Figma */

  /* Mobile dimensions - following Figma segmented control */
  @media (max-width: 768px) {
    padding: 0 var(--general-spacer-02, 0.5rem);
    font-size: 0.8125rem; /* 13px as shown in Figma */
    height: 24px;
    min-width: 44px;
  }

  /* Desktop dimensions - following Figma segmented control */
  @media ${baseVariables.mediaQuery.md} {
    padding: 0 var(--general-spacer-03, 1rem);
    font-size: 0.8125rem; /* 13px consistent with Figma */
    height: 24px;
    min-width: 44px;
  }

  &:hover {
    background: ${props =>
      props.active ? 'var(--colors-base-20, #ffffff)' : 'var(--colors-neutral-10, rgba(244, 244, 244, 0.5))'};
  }
`;

export const ToggleButton = styled.button`
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: var(--t-brand-fg-color, #1c1c1c);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  flex-shrink: 0;
  background: var(--t-icon-btn-primary-bg-color, #eeeeee);
  border-radius: 4px;

  &:hover {
    background: transparent;
    opacity: 0.7;
  }
`;

export const PreviewContent = styled.div`
  /* Fill all remaining height in the flex column, but never overflow it */
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
  background: var(--t-icon-btn-primary-bg-color, #eeeeee);
  display: flex;
  flex-direction: column;

  /* Direct children fill this container via flex — no absolute heights */
  > * {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  /* ── Override SearchResultsComponent internal height rules ──────────────────
     The shop component uses height:100%, min-height:80vh, and calc(100vh-…)
     relative to the viewport. Override so cards scroll within our panel.
  ────────────────────────────────────────────────────────────────────────── */

  /* list-view wrapper: make it flex-column so search-form-container fills remaining space */
  .list-view {
    display: flex !important;
    flex-direction: column !important;
    min-height: 0 !important;
    height: 100% !important;
    overflow: auto !important;
  }

  /* Map view wrapper: position relative so absolutely positioned children work */
  .map-view {
    position: relative !important;
    overflow: visible !important;
    height: 100% !important;

    /* The search-form-container in map view needs to allow overflow for the filter button */
    .search-form-container {
      overflow: visible !important;
    }
  }

  /* The row that holds filter + card list */
  .search-form-container {
    flex: 1 !important;
    min-height: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: auto !important;
    @media (max-width: 767px) {
      overflow: visible !important;
    }

    /* Kill the 80vh min-height on the card container inside this row */
    .search-results-container {
      height: auto !important;
      min-height: 0 !important;
      flex: 1;
    }
  }

  /* ── AllFiltersComponent: style to match Figma design ──────────────────────
     FiltersComponent renders at the top of PreviewContent (inside the card list
     area). The button should be aligned to the right per Figma design.
  ────────────────────────────────────────────────────────────────────────── */

  /* Pagination/sort bar: stretch full width and remove vertical padding */
  .pagination-sort-container {
    max-width: 100%;
    width: 100%;
    padding-left: 8px !important;
    padding-right: 8px !important;
    @media (max-width: 767px) {
      padding-bottom: 0 !important;
    }

    > .container {
      max-width: 100%;
      width: 100%;
    }
  }

  /* The outer filter container: in list view, it's part of the flex layout */
  .filters-container {
    position: relative !important;
    z-index: 9 !important;
    background: var(--t-brand-bg-color, #ffffff) !important;
    border-bottom: 1px solid var(--colors-neutral-20, #eeeeee) !important;
    flex-shrink: 0 !important;
    flex: 0 0 auto !important;
    overflow: visible !important;
    display: block !important;
  }

  /* Map view: position absolutely on top of the map — right side for chat */
  .filters-container.filters-map-view-container {
    position: absolute !important;
    top: 24px !important;
    left: auto !important;
    right: 16px !important;
    background: transparent !important;
    border-bottom: none !important;
    z-index: 100 !important;
    padding: 0 !important;
    @media (max-width: 991px) {
      left: 16px !important;
      right: auto !important;
    }
  }

  /* FiltersComponent inner container: align button to the right */
  .sm-search-result-all-filter-component {
    margin-bottom: 0 !important;
    padding: 0 !important;

    .row-1 {
      display: flex;
      align-items: center !important;
      justify-content: flex-end; /* Align button to the right */
      padding: 0 0 10px 16px;
      margin-top: 12px;
      overflow: visible;
      @media (max-width: 991px) {
        justify-content: flex-start;
        margin-top: 56px;
        padding-left: 0;
      }
    }

    .row-1-container {
      display: flex !important;
      align-items: center !important;
      justify-content: flex-end !important; /* Align button to the right */
    }
  }

  /* Map view: no padding on inner row */
  .filters-map-view-container .sm-search-result-all-filter-component .row-1 {
    padding: 0 !important;
    margin-top: 4px;
    justify-content: flex-start !important; /* Map view: button at its natural position */
    @media (max-width: 991px) {
      margin-top: 40px;
    }
  }

  /* The "All Filters" button container */
  .filter-button {
    display: inline-flex !important;
    align-items: center !important;
  }

  /* The actual All Filters button — override shop styles to match Figma exactly */
  .sm-search-result-all-filter-component-button.all-filter {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 8px !important;
    padding: 0 12px !important;
    margin-left: 8px;
    height: 38px !important;
    border: 1px solid var(--colors-neutral-30, #c4c4c4) !important;
    border-radius: 8px !important;
    background: var(--colors-base-20, #ffffff) !important;
    font-family: 'Swiss721 BT', sans-serif !important;
    font-size: 16px !important;
    font-weight: 500 !important;
    color: var(--colors-neutral-50, #303030) !important;
    cursor: pointer !important;
    white-space: nowrap !important;
    line-height: 1.5 !important;
    box-shadow: none !important;
    text-transform: none !important;
    letter-spacing: 0 !important;
    min-width: unset !important;

    &:hover {
      background: var(--colors-neutral-10, #f6f6f6) !important;
      border-color: var(--colors-neutral-40, #707070) !important;
    }

    &:focus {
      outline: 2px solid var(--colors-base-10, #1c1c1c) !important;
      outline-offset: 2px !important;
    }

    /* Filter icon */
    .icon-dining-filter,
    .icon-s {
      font-size: 16px !important;
      line-height: 1 !important;
      color: inherit !important;
    }

    /* Applied-count badge */
    .all-filter-badge {
      background: var(--colors-base-10, #1c1c1c) !important;
      color: var(--colors-base-20, #ffffff) !important;
      border-radius: 50% !important;
      width: 20px !important;
      height: 20px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      font-size: 12px !important;
      font-weight: 500 !important;
      padding: 0 !important;
      min-width: 20px !important;
    }
  }

  /* Map containers still need 100% height for the canvas to render */
  .map-container,
  .chat-map-container {
    height: 100% !important;
    overflow: hidden !important;
    flex: 1;
  }

  /* Single property response: hide filters button and AI results label */
  .single-property-response {
    .all-filters-container {
      display: none !important;
    }
  }
`;

export const ComponentWrapper = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  max-width: 100%;

  /* Allow property cards to take full width */
  > div {
    width: 100%;
    max-width: none;
  }

  &::-webkit-scrollbar {
    width: 0.5rem;
  }

  &::-webkit-scrollbar-track {
    background: var(--t-icon-btn-primary-bg-color, #eeeeee);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--t-horizontal-vertical-rule, #c4c4c4);
    border-radius: 0.25rem;
  }
`;

export const EmptyState = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--t-brand-alt-color, #707070);
  gap: 1rem;
`;

export const EmptyIcon = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: var(--t-brand-bg-color, #ffffff);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--t-accent-color, #ff8d6b);
  box-shadow: 0 0.25rem 1.25rem 0 rgba(0, 0, 0, 0.07);
`;

export const EmptyTitle = styled.h4`
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--t-brand-fg-color, #1c1c1c);
  margin: 0;
  font-family: Swiss-721, Arial, Helvetica, sans-serif;
`;

export const EmptyDescription = styled.p`
  font-size: 0.875rem;
  color: var(--t-brand-alt-color, #707070);
  margin: 0;
  max-width: 300px;
  line-height: 1.5;
`;

export const LoadingSpinner = styled.div`
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid var(--t-icon-btn-primary-bg-color, #eeeeee);
  border-top: 2px solid var(--t-button-primary-bg-color, #1c1c1c);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

/* ------------------------------------------------------------------
   Sidebar UI (ChatGPT-style)
-------------------------------------------------------------------*/

interface SideNavigation_Props {
  open?: boolean;
}

export const Overlay = styled.div<SideNavigation_Props>`
  display: none;

  @media (max-width: 768px) {
    display: ${props => (props.open ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

export const SidebarRail = styled.aside<SideNavigation_Props>`
  position: fixed;
  left: 0;
  height: calc(100vh - 88px);
  width: 81px;
  background: var(--t-brand-bg-color, #ffffff);
  border-right: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  // gap: 1rem;
  z-index: 998;
  padding-bottom: 2rem;

  .expand-icon {
    display: none;
    cursor: pointer;
  }

  .logo-container:hover {
    .expand-icon {
      display: block;
      // background-color: #f5f5f5;
      // border-radius: 8px;
    }
    .logo {
      display: none;
    }
  }

  @media (max-width: 768px) {
    display: none; /* hide rail on mobile */
  }
`;

export const RailIconButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--t-brand-fg-color, #1c1c1c);

  &:hover {
    background: #f5f5f5;
  }
`;

export const SideNavigation_Container = styled.aside<SideNavigation_Props>`
  height: 100%;
  width: ${props => (props.open ? '250px' : '81px')};
  flex-shrink: 0;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  transition: width 0.5s ease;
  padding: ${props => (props.open ? '24px' : '20px 0')};
  padding-top: 4px;

  .t-font-s {
    font-weight: 700;
  }

  .t-font-m {
    font-weight: 700;
    line-height: 1.25rem;
  }

  .t-subtitle-s {
    font-weight: 700 !important;
  }

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    transform: translateX(${props => (props.open ? '0' : '-100%')});
    transition: transform 0.3s ease;
    width: 310px;
    z-index: 1000;
    padding: 20px;
  }
`;

export const SideNavigation_Header = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const NewChatButton = styled.button`
  margin: 1rem;
  padding: 0.75rem 1rem;
  background: #1c1c1c;
  border: none;
  color: #ffffff;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
`;

export const ChatList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;

  li {
    padding: 0.75rem 0;
    cursor: pointer;
    font-size: 0.875rem;
    color: #1c1c1c;

    &:hover {
      background: transparent;
    }
  }
`;

export const SideNavigation_Footer = styled.div`
  padding: 0;
  margin-top: auto;
  padding-top: 20px;

  button {
    background: none;
    border: none;
    color: var(--t-brand-fg-color, #1c1c1c);
    font-size: 0.875rem;
    cursor: pointer;
    text-align: left;
    width: 100%;

    &:hover {
      text-decoration: none;
      background: transparent;
    }
  }
`;

export const SidebarLogo = styled.div`
  svg,
  img {
    width: 110px;
    height: auto;
  }
`;

export const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  width: 100%;
  border: none;
  background: none;
  font-size: 1rem;
  font-weight: 600;
  color: var(--t-brand-fg-color, #1c1c1c);
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }

  svg {
    width: 20px;
    height: 20px;
    stroke-width: 1.5;
  }
`;

export const ToolIndicator = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.5rem;
  background: rgba(var(--t-accent-color, 255, 141, 107), 0.1);
  border-radius: 1rem;
  font-size: 0.75rem;
  color: var(--t-accent-color, #ff8d6b);
  margin-top: 0.5rem;
  width: fit-content;
`;

export const HotelCard = styled.div`
  background: var(--t-brand-bg-color, #ffffff);
  border-radius: var(--t-cards-border-radius, 14px);
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

export const HotelImage = styled.div`
  width: 100%;
  height: 12.5rem;
  background: var(--t-icon-btn-primary-bg-color, #eeeeee);
  border-radius: var(--t-images-border-radius, 14px);
  margin-bottom: 0.75rem;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 200"><rect fill="%23eeeeee" width="400" height="200"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23707070" font-family="Swiss-721" font-size="14">Hotel Image</text></svg>');
  background-size: cover;
  background-position: center;
`;

export const HotelName = styled.h4`
  font-size: 1rem;
  font-weight: 700;
  color: var(--t-brand-fg-color, #1c1c1c);
  margin: 0 0 0.25rem 0;
  font-family: Swiss-721, Arial, Helvetica, sans-serif;
`;

export const HotelLocation = styled.p`
  font-size: 0.875rem;
  color: var(--t-brand-alt-color, #707070);
  margin: 0 0 0.5rem 0;
`;

export const HotelPrice = styled.div`
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--t-button-primary-bg-color, #1c1c1c);
  margin: 0.5rem 0;
`;

export const HotelRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: var(--t-accent-color, #ff8d6b);
`;

export const FilterBar = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: var(--t-brand-bg-color, #ffffff);
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;
`;

export const FilterChip = styled.button<{ active?: boolean }>`
  padding: 0.375rem 0.75rem;
  border: 1px solid
    ${props =>
      props.active ? 'var(--t-button-primary-bg-color, #1c1c1c)' : 'var(--t-horizontal-vertical-rule, #c4c4c4)'};
  border-radius: 1rem;
  background: ${props =>
    props.active ? 'var(--t-button-primary-bg-color, #1c1c1c)' : 'var(--t-brand-bg-color, #ffffff)'};
  color: ${props => (props.active ? 'var(--t-button-primary-fg-color, #ffffff)' : 'var(--t-brand-fg-color, #1c1c1c)')};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: Swiss-721, Arial, Helvetica, sans-serif;

  &:hover {
    border-color: var(--t-button-primary-bg-color, #1c1c1c);
    background: ${props =>
      props.active ? 'var(--t-button-primary-bg-color, #1c1c1c)' : 'var(--t-icon-btn-primary-bg-color, #eeeeee)'};
  }
`;

export const MapContainer = styled.div`
  height: 100%;
  width: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--t-brand-bg-color, #ffffff);
  /*position: static; Ensure no relative positioning that interferes with Google Maps */

  @media (max-width: 768px) {
    border-radius: 0;
  }

  .gm-style {
    font-family: Swiss-721, Arial, Helvetica, sans-serif;
  }

  .gm-style-iw {
    font-family: Swiss-721, Arial, Helvetica, sans-serif;
  }
`;

export const MapInfoCard = styled.div`
  padding: 0.75rem;
  min-width: 201px;
  max-width: 250px;
  font-family: Swiss-721, Arial, Helvetica, sans-serif;

  h4 {
    margin: 0 0 0.5rem 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--t-brand-fg-color, #1c1c1c);
    line-height: 1.3;
  }

  p {
    margin: 0 0 0.5rem 0;
    font-size: 0.75rem;
    line-height: 1.4;

    &.address {
      color: var(--t-brand-alt-color, #666666);
    }

    &.price {
      font-weight: 600;
      color: var(--t-brand-fg-color, #1c1c1c);
      font-size: 0.875rem;
    }

    &.rating {
      color: var(--t-brand-alt-color, #666666);
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const MobileToggle = styled.button`
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  z-index: 1000;

  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;

  border: 1px solid var(--t-horizontal-vertical-rule, #c4c4c4);
  border-radius: 0.375rem;
  background: var(--t-brand-bg-color, #ffffff);
  color: var(--t-brand-alt-color, #707070);
  font-size: 1.25rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 769px) {
    display: none;
  }

  &:hover {
    background: var(--t-icon-btn-primary-bg-color, #eeeeee);
  }
`;
// Personalized Suggestion Chips
export const SuggestionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex-flow: wrap;
`;

export const SuggestionsLabel = styled.div`
  font-size: 0.875rem;
  color: var(--t-brand-fg-color, #1c1c1c);
  font-weight: 600;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  &::before {
    content: '✨';
    font-size: 1rem;
  }
`;

export const SuggestionsChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const SuggestionChip = styled.button`
  padding: 0.625rem 1rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  color: var(--t-brand-fg-color, #1c1c1c);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 1.25rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: Swiss-721, Arial, Helvetica, sans-serif;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
  &:hover {
    transform: translateY(-2px);
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: #ffffff;
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  &:active {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
  }
  @media (max-width: 768px) {
    padding: 0.5rem 0.875rem;
    font-size: 0.8125rem;
  }
`;

export const ChatFiltersGlobalStyle = createGlobalStyle`
  .chat-filters {
    @media (max-width: 991px) {
      .pop-up-footer {
        flex-direction: row;
      }
    }
  }
`;
